import React, { useMemo, useState } from "react";
import { Row, Col, Spinner, Button } from "react-bootstrap";
import styled from "styled-components";
import AddShoppingCart from "@mui/icons-material/AddShoppingCart";
import CheckIcon from "@mui/icons-material/Check";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SuccessButton from "../../../organisms/SuccessButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Calendar from "../../../organisms/Calendar";
import { HeaderSection } from "../HeaderSection";
import { Title } from "../Title";
import ServerErrorModal from "../../../organisms/ServerErrorModal";
import { useMutation } from "@tanstack/react-query";
import {
  buildNuevoAgendamientoSpa,
  buildNuevoItemCarritoSpa,
  Model,
} from "../Model";
import CurrencyFormat from "react-currency-format";
import { parseCurrency } from "../../../../utils/numberUtils";
import ExitoCard, { ExitoOption } from "../../../organisms/ExitoCard";
import PaymentIcon from "@mui/icons-material/Payment";
import Pagar from "../Pagar/Pagar";
import {
  PrettyHorarioSpa,
  agendarSpa,
} from "../../../../services/agendamientosSpaServicios";
import { useWizard } from "../../../../hooks/wizard";
import Fechas from "../Fechas/Fechas";
import { useCarrito } from "../../../../hooks/carrito";
import InformationModal from "../../../organisms/InformationModal";

function Resumen({ model }: { model: Model }) {
  const [newModel, setNewModel] = useState(model);
  const { avanza, vuelve, volver, avanzar } = useWizard();
  const {
    preparationOperationsIsLoading: carritoPreparationOperationsIsLoading,
    errorValidacionInclusionCarrito,
    validarInclusion,
    setErrorValidacionInclusionCarrito,
    mutarAgregarAlCarrito,
    agregarAlCarritoIsLoading,
    agregarAlCarritoIsSuccess,
    isError: carritoOperationsIsError,
  } = useCarrito();

  const total = useMemo(() => {
    const subTotal =
      (newModel.valorFinalTarifa || 0) * newModel.fechasProgramadas.length;
    return subTotal - newModel.descuento;
  }, [newModel]);

  const {
    isError: mutacionAgendarIsError,
    isLoading: mutacionAgendarIsLoading,
    isSuccess: mutacionIsSuccess,
    mutate: mutateAgendar,
  } = useMutation(agendarSpa);

  const isError = mutacionAgendarIsError || carritoOperationsIsError;
  const isSuccess = mutacionIsSuccess || agregarAlCarritoIsSuccess;

  const onValorFinalTarifaChange = (
    val: React.ChangeEvent<HTMLInputElement>
  ) => {
    setNewModel((prevModel) => {
      const modelActualizado = {
        ...prevModel,
        valorFinalTarifa: parseCurrency(val.target.value, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onDescuentoChange = (val: React.ChangeEvent<HTMLInputElement>) => {
    setNewModel((prevModel) => {
      const targetVal: string | undefined = val.target.value;
      const targetValStr: string = targetVal ? targetVal : "$0";
      const modelActualizado = {
        ...prevModel,
        descuento: parseCurrency(targetValStr, "$", ","),
      };
      return recalcularTotal(modelActualizado);
    });
  };

  const onConfirmarClick = () => {
    const agendamiento = buildNuevoAgendamientoSpa(newModel);
    mutateAgendar(agendamiento);
  };

  const onAgregarAlCarritoClick = () => {
    const itemCarrito = buildNuevoItemCarritoSpa(newModel);
    const isExito = validarInclusion(itemCarrito);

    if (isExito) {
      mutarAgregarAlCarrito(itemCarrito);
    }
  };

  const onModalValidacionInclusionCarritoClose = () => {
    setErrorValidacionInclusionCarrito(undefined);
  };

  if (isSuccess) {
    return <ExitoCard title={"¡Reserva exitosa!"} options={exitoOptions} />;
  }

  if (avanza) {
    return <Pagar model={newModel} />;
  }

  if (vuelve) {
    return <Fechas model={newModel} />;
  }

  return (
    <>
      {isError && <ServerErrorModal />}
      {errorValidacionInclusionCarrito && (
        <InformationModal
          message={errorValidacionInclusionCarrito.mensaje}
          onClose={onModalValidacionInclusionCarritoClose}
        />
      )}
      <Row className="justify-content-center">
        <Col className="col-11">
          <HeaderSection className="pt-5 mb-4">
            <Title>
              Resúmen de agendamiento
              <IconContainer>
                <FactCheckIcon sx={{ fontSize: 28 }} />
              </IconContainer>
            </Title>
          </HeaderSection>
          <section className="mb-5">
            <SectionTitle className="mb-3">Datos básicos</SectionTitle>
            <InfoRow label="Propietario" value={model.nombresPropietario} />
            <InfoRow label="Mascota" value={model.nombreMascota} />
            <InfoRow label="Horario" value={PrettyHorarioSpa[model.horario]} />
            <div>
              <label className="mb-2" style={{ fontWeight: 500 }}>
                Fechas programadas:
              </label>
              <StyledCalendar
                preSelectedDates={model.fechasProgramadas}
                disableAll={true}
              />
            </div>
          </section>

          <section className="mb-4">
            <SectionTitle className="mb-3">Tarifas y planes</SectionTitle>
            {model.tarifa && (
              <Row className="mb-2">
                <LabelCol className="col-5">
                  {model.tarifa!!.nombre + ":"}
                </LabelCol>
                <Col></Col>
                <Col className="text-right">
                  <CurrencyFormat
                    name="valorFinalTarifa"
                    value={newModel.valorFinalTarifa}
                    thousandSeparator={","}
                    onChange={onValorFinalTarifaChange}
                    prefix={"$"}
                    decimalSeparator={"."}
                  />
                </Col>
              </Row>
            )}
            <Row className="mb-2">
              <LabelCol className="col-5">Descuento:</LabelCol>
              <Col></Col>
              <Col className="text-right">
                <CurrencyFormat
                  name="descuento"
                  value={newModel.descuento}
                  thousandSeparator={","}
                  onChange={onDescuentoChange}
                  prefix={"$"}
                  decimalSeparator={"."}
                />
              </Col>
            </Row>
            <Row className="mb-2">
              <LabelCol className="col-5">Total: </LabelCol>
              <Col></Col>
              <Col className="text-right">
                <CurrencyFormat
                  value={total}
                  thousandSeparator={","}
                  disabled
                  prefix={"$"}
                  decimalSeparator={"."}
                />
              </Col>
            </Row>
          </section>

          <Row className="mb-2 justify-content-between">
            <Col>
              <Button
                type={"button"}
                onClick={volver}
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
                Volver
              </Button>
            </Col>
            <Col className="d-flex justify-content-end gap-2">
              <Button
                variant="primary"
                onClick={onAgregarAlCarritoClick}
                size="sm"
                disabled={carritoPreparationOperationsIsLoading}
              >
                {!agregarAlCarritoIsLoading && (
                  <AddShoppingCart sx={{ fontSize: 18, marginRight: "2px" }} />
                )}
                {agregarAlCarritoIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                Agregar
              </Button>
              <Button variant="primary" onClick={avanzar} size="sm">
                <PaymentIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                Pagar
              </Button>
              <SuccessButton
                type={"button"}
                onClick={onConfirmarClick}
                variant="success"
                size="sm"
              >
                {!mutacionAgendarIsLoading && (
                  <CheckIcon sx={{ fontSize: 18, marginRight: "2px" }} />
                )}
                {mutacionAgendarIsLoading && (
                  <Spinner animation="border" size="sm" />
                )}
                Finalizar{" "}
              </SuccessButton>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
}

const recalcularTotal = (m: Model): Model => {
  const subTotal = (m.valorFinalTarifa || 0) * m.fechasProgramadas.length;
  const total = subTotal - m.descuento;
  return { ...m, total };
};

const SectionTitle = styled("h5")`
  font-weight: 500;
`;

const InfoRow = ({ label, value }: { label: string; value?: string }) => {
  return (
    <Row className="mb-2">
      <LabelCol className="col-4">{label + ":"}</LabelCol>
      <Col className="text-right">{value}</Col>
    </Row>
  );
};

const LabelCol = styled(Col)`
  font-weight: 500;
`;

const StyledCalendar = styled(Calendar)`
  width: 100%;
  border-radius: 5px;
  padding: 0%;
`;

const IconContainer = styled("span")`
  display: relative;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.main};
`;

const exitoOptions: ExitoOption[] = [
  {
    description: "Realizar nuevo agendamiento",
    path: "/reservas",
    variant: "primary",
  },
  {
    description: "Ver listado",
    path: "/reservas/spa/listar",
    variant: "primary",
  },
  {
    description: "Ver carrito",
    path: "/carrito",
    variant: "secondary",
    isLast: false
  },
];

export default Resumen;
